/**
 * Catches, interceptions and swats (owner: ball team; ARCHITECTURE §5 "Ball", CATCH tuning).
 *  - CATCH: the swept closest approach of the ball to an OPEN / REACH glove of a player whose glove is free is
 *    within the catch radius (× Hail Mary for the thrower). Offense = completion (only where the rules allow it:
 *    ball AND head beyond the LOS — `mayTouchFlight`), defense = INTERCEPTION, only with a COMMITTED glove (a
 *    dive / leap REACH, or airborne after a jump — `mayCatchFlight`); a passive grounded defender's glove only
 *    swats. Both on one tick: CATCH.tieGoesToOffense.
 *  - Release grace: for CATCH.releaseGraceFrames after the release a non-thrower's non-lunging glove cannot touch
 *    the ball at all (`inReleaseGrace`; his head still can).
 *  - SWAT: inside the swat radius of a free glove — an open glove only once the ball is leaving (a near miss, not
 *    a catch still about to happen), any other pose on the way in — or touching a head while approaching it
 *    (CATCH.headSwat): the ball deflects (restitution + CATCH.swatImpulse + tumble), stays AIR and catchable.
 *    The swatter cannot touch the flight again until it leaves his reach (PF.SWATTED; no re-swat spam).
 *  - Bookkeeping for the rules: ball.touchedMask / lastTouchedBy, fgAttempt cleared by any catch.
 *  - Baits: a defender's fast-fall pays off when he then intercepts or swats.
 * Per-tick measurements go through fixed scratch arrays (no doubles cross a call boundary: allocation-free).
 */
import { CATCH, DT, GLOVE_RADIUS, HITSTOP, MOTION, PHYSICS, SPECIALS_TUNING, yardsToGoal } from '@hf/shared';
import { addHitStop, defenderPayoffBait } from '../contact/fx';
import { BallState, CommitKind, GlovePose, PlayerState } from '../enums';
import { PF, setState } from '../player/fsm';
import { effectiveHeadRadius } from '../specials/query';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';
import { kickKindIndex } from './loose';
import { gloveFree, inReleaseGrace, mayCatchFlight, mayTouchFlight } from './reach';

const KICK_KINDS = ['kickoff', 'onside', 'punt'] as const;
import { dir, setRotation, support, supportAlongDir } from './shape';

/** Per-player scratch (index = player). */
const touchable = [0, 0];
const gloveX = [0, 0];
const gloveY = [0, 0];
const gloveD = [0, 0];
/** Swept closest approach to the glove this tick. */
const gloveMin = [0, 0];
/** 1 = the ball is moving away from the glove (relative), 0 = closing. */
const gloveOut = [0, 0];
const headD = [0, 0];
/** Head radius + the ball's extent toward the head. */
const headReach = [0, 0];
/** 1 = the ball is moving into the head (relative). */
const headIn = [0, 0];
/** 1 = an OPEN / REACH free glove. */
const open = [0, 0];
/** 1 = that glove may CATCH this flight (offense receiver, or a committed defender glove). */
const canCatch = [0, 0];
/** 1 = the glove may touch the ball at all this tick (not in the release grace). */
const gloveOk = [0, 0];

const isOpenGlove = (p: PlayerSim): boolean =>
  gloveFree(p) && (p.glovePose === GlovePose.OPEN || p.glovePose === GlovePose.REACH);

/** Fill the scratch for player `i` (or mark him untouchable). The ball rotation must be in `rot`. */
const measure = (s: SimState, i: number): void => {
  const b = s.ball;
  const p = s.players[i as 0 | 1];
  if ((p.flags & PF.SWATTED) !== 0) {
    // re-arm once the ball has left his reach
    const gx = b.x - (p.x + p.facing * p.gloveX);
    const gy = b.y - (p.y + p.gloveY);
    const hx = b.x - p.x;
    const hy = b.y - p.y;
    const rg = CATCH.gloveSwatRadius + MOTION.swatRearm;
    const rh = p.stats.headRadius + GLOVE_RADIUS + MOTION.swatRearm;
    if (gx * gx + gy * gy > rg * rg && hx * hx + hy * hy > rh * rh) p.flags &= ~PF.SWATTED;
  }
  if (!mayTouchFlight(s, p)) {
    touchable[i] = 0;
    return;
  }
  touchable[i] = 1;
  gloveOk[i] = inReleaseGrace(s, p) ? 0 : 1;
  open[i] = gloveOk[i] === 1 && isOpenGlove(p) ? 1 : 0;
  canCatch[i] = open[i] === 1 && mayCatchFlight(s, p) ? 1 : 0;
  const rvx = b.vx - p.vx;
  const rvy = b.vy - p.vy;
  // glove
  const wx = p.x + p.facing * p.gloveX;
  const wy = p.y + p.gloveY;
  gloveX[i] = wx;
  gloveY[i] = wy;
  const gx = b.x - wx;
  const gy = b.y - wy;
  gloveD[i] = Math.sqrt(gx * gx + gy * gy);
  gloveOut[i] = gx * rvx + gy * rvy >= 0 ? 1 : 0;
  // swept: closest approach along the relative path of this tick
  const dx = rvx * DT;
  const dy = rvy * DT;
  const x0 = gx - dx;
  const y0 = gy - dy;
  const dd = dx * dx + dy * dy;
  let t = dd > 1e-18 ? -(x0 * dx + y0 * dy) / dd : 1;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = x0 + dx * t;
  const cy = y0 + dy * t;
  gloveMin[i] = Math.sqrt(cx * cx + cy * cy);
  // head
  const hx = b.x - p.x;
  const hy = b.y - p.y;
  const hd = Math.sqrt(hx * hx + hy * hy);
  headD[i] = hd;
  headIn[i] = hx * rvx + hy * rvy < 0 ? 1 : 0;
  const hr = effectiveHeadRadius(p);
  if (hd > 1e-9) {
    dir.x = -hx / hd;
    dir.y = -hy / hd;
    supportAlongDir();
    headReach[i] = hr + support.h;
  } else headReach[i] = hr;
};

/** Catch radius check for player `i` (Hail Mary: the thrower's own radius grows). */
const catches = (s: SimState, i: number): boolean => {
  if (touchable[i] === 0 || canCatch[i] === 0) return false;
  const b = s.ball;
  const r =
    b.hailMary && b.thrownBy === i
      ? CATCH.gloveCatchRadius * SPECIALS_TUNING.hailMary.catchRadiusMul
      : CATCH.gloveCatchRadius;
  return gloveMin[i]! <= r;
};

const near = (ax: number, ay: number, bx: number, by: number, r: number): boolean => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy <= r * r;
};

const doCatch = (s: SimState, ev: SimEventSink, i: number): void => {
  const b = s.ball;
  const p = s.players[i as 0 | 1];
  const other = s.players[i === 0 ? 1 : 0];
  const thrower = b.thrownBy;
  const kick = b.kick;
  const contested =
    p.glovePose === GlovePose.REACH ||
    near(b.x, b.y, other.x, other.y, MOTION.contestRadius) ||
    near(b.x, b.y, other.x + other.facing * other.gloveX, other.y + other.gloveY, MOTION.contestRadius);
  b.state = BallState.HELD;
  b.holder = p.index;
  b.fgAttempt = false;
  b.kick = false;
  b.touchedMask |= 1 << p.index;
  b.lastTouchedBy = p.index;
  b.x = gloveX[i]!;
  b.y = gloveY[i]!;
  b.vx = p.vx;
  b.vy = p.vy;
  b.angVel = 0;
  p.flags |= PF.LUNGE_DONE;
  p.flags &= ~PF.SWATTED;
  p.commitKind = CommitKind.NONE; // the jump / lunge did its job
  setState(p, PlayerState.CATCH);
  if (kick) {
    // Classic: the receiver fields a kick in the air (only he may touch it) — a live return.
    if (ev) {
      ev.push({
        type: 'KICK_CAUGHT',
        tick: s.tick,
        actor: p.index,
        x: b.x,
        y: b.y,
        kind: KICK_KINDS[kickKindIndex(s.rules.playCall)]!,
        fromGround: false,
        inEndZone: yardsToGoal(p.x, s.rules.attackDir) <= 0,
      });
    }
    addHitStop(s, ev, p.index, HITSTOP.catch, 'catch', b.x, b.y);
    return;
  }
  if (p.index === s.rules.offense) {
    if (ev)
      ev.push({
        type: 'CATCH',
        tick: s.tick,
        actor: p.index,
        x: b.x,
        y: b.y,
        airYards: Math.abs(b.x - b.releaseX),
        contested,
      });
    addHitStop(s, ev, p.index, HITSTOP.catch, 'catch', b.x, b.y);
  } else {
    if (ev) ev.push({ type: 'INTERCEPTION', tick: s.tick, actor: p.index, x: b.x, y: b.y, thrower });
    addHitStop(s, ev, p.index, HITSTOP.interception, 'interception', b.x, b.y);
    defenderPayoffBait(s, ev, p, other, false);
  }
};

/** Deflect the ball off player `i`'s glove (byGlove) or head, moving with the player. */
const doSwat = (s: SimState, ev: SimEventSink, i: number, byGlove: boolean): void => {
  const b = s.ball;
  const p = s.players[i as 0 | 1];
  let nx = b.x - (byGlove ? gloveX[i]! : p.x);
  let ny = b.y - (byGlove ? gloveY[i]! : p.y);
  const d = Math.sqrt(nx * nx + ny * ny);
  if (d < 1e-9) {
    nx = p.facing;
    ny = 0;
  } else {
    nx /= d;
    ny /= d;
  }
  let rvx = b.vx - p.vx;
  let rvy = b.vy - p.vy;
  const vn = rvx * nx + rvy * ny;
  const vt = -rvx * ny + rvy * nx;
  if (vn < 0) {
    rvx -= (1 + MOTION.swatRestitution) * vn * nx;
    rvy -= (1 + MOTION.swatRestitution) * vn * ny;
  }
  b.vx = p.vx + rvx + nx * CATCH.swatImpulse;
  b.vy = p.vy + rvy + ny * CATCH.swatImpulse;
  b.angVel += MOTION.deflectSpinMul * PHYSICS.ballSpinPerSpeed * vt;
  b.touchedMask |= 1 << p.index;
  b.lastTouchedBy = p.index;
  p.flags |= PF.SWATTED;
  if (p.index === b.thrownBy) return; // a QB bumping his own pass: physics only
  if (ev)
    ev.push({ type: 'SWAT', tick: s.tick, actor: p.index, x: b.x, y: b.y, by: byGlove ? 'glove' : 'head' });
  addHitStop(s, ev, p.index, HITSTOP.swat, 'swat', b.x, b.y);
  if (p.index !== s.rules.offense) defenderPayoffBait(s, ev, p, s.players[i === 0 ? 1 : 0], false);
};

/**
 * Catch / swat detection for a live flight. Call after the flight moved this tick. One catch or one deflection
 * per tick; for deflections the closest contact wins.
 */
export const catchOrSwat = (s: SimState, ev: SimEventSink): void => {
  setRotation(s.ball);
  measure(s, 0);
  measure(s, 1);
  const c0 = catches(s, 0);
  const c1 = catches(s, 1);
  if (c0 || c1) {
    let winner = c0 ? 0 : 1;
    if (c0 && c1) {
      if (CATCH.tieGoesToOffense) winner = s.rules.offense;
      else winner = gloveMin[0]! <= gloveMin[1]! ? 0 : 1;
    }
    doCatch(s, ev, winner);
    return;
  }
  let best = -1;
  let bestGlove = false;
  let bestD = Infinity;
  for (let i = 0; i < 2; i++) {
    if (touchable[i] === 0) continue;
    const p = s.players[i as 0 | 1];
    const gd = gloveD[i]!;
    // a glove that could catch swats only a near miss (ball leaving); any other free glove swats on the way in
    if (
      gloveOk[i] === 1 &&
      gloveFree(p) &&
      gd <= CATCH.gloveSwatRadius &&
      gd < bestD &&
      (canCatch[i] === 0 || gloveOut[i] === 1)
    ) {
      best = i;
      bestGlove = true;
      bestD = gd;
    }
    if (CATCH.headSwat && headIn[i] === 1 && headD[i]! <= headReach[i]!) {
      const hd = headD[i]! - effectiveHeadRadius(p);
      if (hd < bestD) {
        best = i;
        bestGlove = false;
        bestD = hd;
      }
    }
  }
  if (best >= 0) doSwat(s, ev, best, bestGlove);
};
